import { observable, action } from "mobx";
import CustomerRepository from "~/repository/CustomerRepository";
import CachedRepository from "~/utils/CachedRepository";
import FormStore from "~/stores/FormStore";
import CustomerPaymentMethodRepository from "~/repository/CustomerPaymentMethodRepository";
import Customer from "~/models/Customer";
import NestStore from "~/stores/NestStore";

class CreateCustomerStore extends NestStore {
  @observable customerPaymentMethods = [];

  @observable formStore = null;

  @observable selectedAddressResult = null;

  @observable addressResults = [];

  /**
   * @property {string} photo base64 encoded
   */
  @observable photoData = null;

  /**
   * @property {string} logo base64 encoded
   */
  @observable logoData = null;

  @observable customer = null;

  customerRepository = null;

  customerPaymentMethodRepository = null;

  cachedRepository = null;

  constructor() {
    super();

    this.formStore = new FormStore();
    this.customerRepository = new CustomerRepository();
    this.customerPaymentMethodRepository = new CustomerPaymentMethodRepository();
    this.cachedRepository = new CachedRepository();
  }

  @action
  clear() {
    this.isLoading = false;
    this.isSaving = false;
    this.selectedAddressResult = null;
    this.addressResults = [];
    this.photo = null;
    this.logo = null;
    this.customer = null;
    this.formStore = new FormStore();
  }

  @action
  load() {
    this.isLoading = true;

    return this.cachedRepository
      .cacheResponse("customerPaymentMethods", () => {
        return this.customerPaymentMethodRepository.fetch();
      })
      .then(response => {
        this.customerPaymentMethods = Object.keys(response.data.customerPaymentMethods).map(key => {
          return {
            key,
            value: key,
            text: response.data.customerPaymentMethods[key],
          };
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  setPhotoData(encoded) {
    this.photoData = encoded;
  }

  @action
  setLogoData(encoded) {
    this.logoData = encoded;
  }

  @action
  setAddressResults(addressResults) {
    this.addressResults = addressResults;
  }

  @action
  setSelectedAddressResult(selectedAddressResult) {
    this.selectedAddressResult = selectedAddressResult;
  }

  @action
  submit({
    firstName,
    lastName,
    email,
    company = null,
    contactPhone,
    cellPhone = null,
    website = null,
    internalNotes = null,
    assistantCcEmails = null,
    defaultPaymentCollectionMethod = null,
    street,
    street2,
    city,
    country,
    state,
    zip,
  }) {
    if (this.isSaving) {
      return Promise.resolve();
    }

    const payload = {
      firstName,
      lastName,
      email,
      contactPhone,
      address: {
        street,
        street2,
        city,
        country,
        state,
        zip,
      },
    };

    if (company) {
      payload.company = company;
    }

    if (cellPhone) {
      payload.cellPhone = cellPhone;
    }

    if (website) {
      payload.website = website;
    }

    if (internalNotes) {
      payload.internalNotes = internalNotes;
    }

    if (assistantCcEmails) {
      payload.assistantCcEmails = assistantCcEmails;
    }

    if (defaultPaymentCollectionMethod) {
      payload.defaultPaymentCollectionMethod = defaultPaymentCollectionMethod;
    }

    if (this.photoData) {
      payload.photoData = this.photoData;
    }

    if (this.logoData) {
      payload.logoData = this.logoData;
    }

    this.isSaving = true;
    this.errorMessages = {};
    this.errorMessage = "";

    return this.customerRepository
      .create(payload)
      .then(response => {
        this.customer = new Customer(response.data.customer);
        this.isSaving = false;
      })
      .catch(e => {
        this.isSaving = false;

        this.setErrorMessages(e);

        throw e;
      });
  }
}

export default CreateCustomerStore;
